import { useCallback, useEffect, useRef, useState } from 'react'

import {
  adoptPending,
  canAddMore,
  rejectReason,
  type DraftAttachment,
  type RejectReason,
} from './attachments'
import { deleteAttachment, uploadAttachment, usePendingAttachments } from './queries'

/**
 * 会话附件的上传编排。
 *
 * # 为什么它是一个 hook 而不是散在 index.tsx 里
 *
 * 这一块有五件事要同时成立，而它们**每一件的错法都是静默的**：
 *
 *   1. 换会话时把上一条的草稿附件清掉（不清的话，A 会话传的文件会跟着
 *      跑到 B 会话的输入框上，然后在发送时被服务端拒——而报错说的是
 *      「有附件挂不上去」，指不向真正的原因）
 *   2. 进页面时接管服务端留着的那些（不接管的话它们永远躺在库里，
 *      用户既看不到也删不掉）
 *   3. 组件卸载时把在飞的上传取消掉（不取消的话，一个已经不在屏幕上的
 *      上传完成之后会去 setState 一个卸载了的组件）
 *   4. 重试要复用同一行，不是新加一行
 *   5. 发送成功之后清空——而**只清成功的那些**，失败的留着让他再试
 *
 * 放在组件里，这五件只有「跑起来看看」这一种验法。
 */
export function useConversationAttachments(conversationId: string | undefined) {
  const [items, setItems] = useState<readonly DraftAttachment[]>([])
  const [notice, setNotice] = useState<RejectReason | 'too_many' | null>(null)
  // 每一次上传的 AbortController，按本地 id 存。
  const inflight = useRef(new Map<string, AbortController>())
  // 原始 File 存着，重试要用它——重试不该要求用户再选一次文件。
  const files = useRef(new Map<string, File>())
  const pending = usePendingAttachments(conversationId)
  // 换会话时接管一次。用 ref 记住「这条会话接管过了」，
  // 否则每次轮询回来都会把用户刚删掉的那些又加回去。
  const adopted = useRef<string | undefined>(undefined)

  // ① 换会话：把上一条的草稿附件清干净，并把在飞的取消掉。
  useEffect(() => {
    for (const [, controller] of inflight.current) controller.abort()
    inflight.current.clear()
    files.current.clear()
    setItems([])
    setNotice(null)
    adopted.current = undefined
  }, [conversationId])

  // ② 接管服务端留着的那些。**它们全是 done**——服务端有那一行就等于
  //    传完了，标成 uploading 会让重进页面时凭空出现走不完的进度条。
  useEffect(() => {
    if (conversationId === undefined) return
    if (adopted.current === conversationId) return
    const list = pending.data?.attachments
    if (!list) return
    adopted.current = conversationId
    if (list.length === 0) return
    setItems((prev) => {
      const have = new Set(prev.map((a) => a.serverId).filter(Boolean))
      const extra = adoptPending(list).filter((a) => !have.has(a.serverId))
      return [...prev, ...extra]
    })
  }, [conversationId, pending.data])

  // ③ 卸载时取消在飞的上传。
  useEffect(() => {
    const map = inflight.current
    return () => {
      for (const [, controller] of map) controller.abort()
      map.clear()
    }
  }, [])

  const runUpload = useCallback(
    (localId: string, file: File, cid: string) => {
      const controller = new AbortController()
      inflight.current.set(localId, controller)
      void (async () => {
        try {
          const uploaded = await uploadAttachment({
            conversationId: cid,
            file,
            signal: controller.signal,
            onProgress: (percent) =>
              setItems((prev) =>
                prev.map((a) => (a.localId === localId ? { ...a, percent } : a)),
              ),
          })
          setItems((prev) =>
            prev.map((a) =>
              a.localId === localId
                ? { ...a, state: 'done' as const, percent: 100, serverId: uploaded.id, error: undefined }
                : a,
            ),
          )
        } catch (err) {
          // 取消不是失败：那一行已经被换会话/卸载那条路清掉了。
          if (err instanceof DOMException && err.name === 'AbortError') return
          setItems((prev) =>
            prev.map((a) =>
              a.localId === localId
                ? // 服务端那句人话原样带上。**读不出来就留空**，
                  // 由渲染层落到兜底那句——编一个原因会把用户送去
                  // 改一个没问题的地方。
                  { ...a, state: 'failed' as const, error: (err as Error).message }
                : a,
            ),
          )
        } finally {
          inflight.current.delete(localId)
        }
      })()
    },
    [],
  )

  /** 选中了几个文件。传之前先看一眼，被拦下的**不进列表**。 */
  const add = useCallback(
    (picked: readonly File[]) => {
      if (conversationId === undefined) return
      setNotice(null)
      // **先在本地算完，再一次性 setState 并发起上传。**
      //
      // 第一版把这两件事写在 setItems 的更新函数里，那是在 reducer 里做
      // 副作用——React 严格模式会把更新函数跑两遍，于是每个文件会被传两次，
      // 而屏幕上只有一行。这类 bug 在开发模式下才现形，
      // 一旦漏过去，线上表现是「库里莫名其妙多了一半的孤儿附件」。
      setItems((prev) => {
        const next = [...prev]
        const toUpload: { localId: string; file: File }[] = []
        let blocked: RejectReason | 'too_many' | null = null
        for (const file of picked) {
          // 一次选 5 个而只剩 2 个位置时，前 2 个收下、后 3 个被拦——
          // 整批拒掉会让他重选一遍。
          if (!canAddMore(next)) {
            blocked = 'too_many'
            continue
          }
          const why = rejectReason({ size: file.size, type: file.type })
          if (why) {
            blocked = why
            continue
          }
          const localId = 'l-' + crypto.randomUUID()
          toUpload.push({ localId, file })
          next.push({
            localId,
            name: file.name,
            bytes: file.size,
            mime: file.type,
            state: 'uploading' as const,
            percent: 0,
          })
        }
        // 副作用挪到更新函数外面去跑。用 queueMicrotask 而不是直接调：
        // 直接调仍然在渲染阶段里，React 会警告「在渲染时更新另一个组件」。
        queueMicrotask(() => {
          for (const item of toUpload) {
            files.current.set(item.localId, item.file)
            runUpload(item.localId, item.file, conversationId)
          }
          if (blocked) setNotice(blocked)
        })
        return next
      })
    },
    [conversationId, runUpload],
  )

  /** 重试一个失败的。**复用同一行**，不新加一行。 */
  const retry = useCallback(
    (localId: string) => {
      if (conversationId === undefined) return
      const file = files.current.get(localId)
      if (!file) return
      setItems((prev) =>
        prev.map((a) =>
          a.localId === localId
            ? { ...a, state: 'uploading' as const, percent: 0, error: undefined }
            : a,
        ),
      )
      runUpload(localId, file, conversationId)
    },
    [conversationId, runUpload],
  )

  /** 「不要了」。传上去了的顺手在服务端也摘掉，别留孤儿。 */
  const remove = useCallback(
    (localId: string) => {
      const target = items.find((a) => a.localId === localId)
      inflight.current.get(localId)?.abort()
      inflight.current.delete(localId)
      files.current.delete(localId)
      setItems((prev) => prev.filter((a) => a.localId !== localId))
      setNotice(null)
      if (conversationId !== undefined && target?.serverId) {
        // 服务端那次删失败**不回滚界面**：用户已经说了不要，
        // 把它变回来他会以为自己点错了。留下的孤儿由待发列表兜底
        // （下次进来还看得到它，还能再删一次）。
        void deleteAttachment(conversationId, target.serverId).catch(() => {})
      }
    },
    [conversationId, items],
  )

  /**
   * 发送成功之后。**只清被带走的那些**，失败的留着让他再试。
   *
   * 清全部会把一个刚失败、他正打算重试的文件也一起抹掉——
   * 而那个文件的字节只在这个浏览器里，抹掉就再也拿不回来了。
   */
  const clearSent = useCallback((sentIDs: readonly string[]) => {
    const sent = new Set(sentIDs)
    setItems((prev) =>
      prev.filter((a) => !(a.serverId !== undefined && sent.has(a.serverId))),
    )
    setNotice(null)
  }, [])

  return { items, notice, add, retry, remove, clearSent }
}
